import { CUSTOMER_AGGREGATIONS_SPACE_PARAMETER_SUPPORT } from '@src/components/AppLayout/Panels/Dealer/DealerSpaceParameters';
import { useSpaceParameterControlledFilter } from '@src/components/AppLayout/SpaceParameters';
import { useMarketAccountsFilter, useMarketsFilter, useModeFilter } from '@src/hooks';
import { useAggregationsQuery } from '@src/hooks/useAggregationRequests';
import {
  EMPTY_ARRAY,
  EntityAdmin,
  useAggregationsContext,
  useMarketAccountsContext,
  useMarketsContext,
  type ExtendedAggregationForEA,
} from '@talos/kyoko';
import { useMemo } from 'react';
import { usePricingAggregationsFilter } from '../../../hooks/filters/usePricingAggregationsFilter';
import { useCustomerAggregationsDelete } from './useCustomerAggregationsDelete';
import {
  createIsMarketAccountInCustomerAggregationFilter,
  CUSTOMER_AGGREGATION_NAME_AND_DISPLAY_NAME_OPTIONS,
  CUSTOMER_AGGREGATIONS_ALLOW_EDIT_ENTITY,
  CUSTOMER_AGGREGATIONS_ALLOW_MODE_SWITCH,
  CUSTOMER_AGGREGATIONS_COLUMNS,
  CUSTOMER_AGGREGATIONS_GROUP_COLUMN_DEF,
  getCustomerAggregationsDeletePath,
  getCustomerAggregationsEntityDisplayName,
  getCustomerAggregationsEntityDrawerOptions,
  getCustomerAggregationsEntityForPatchPost,
  getCustomerAggregationsIsRowBulkEditSelectable,
  getCustomerAggregationsMarketsDrawerOptionsFromContext,
  getCustomerAggregationsNewEntityDefault,
  getCustomerAggregationsPatchPath,
  getCustomerAggregationsPostPath,
  getCustomerAggregationsResponseAsBlotterEntity,
  getCustomerAggregationsShouldUsePatchForPost,
  getCustomerAggregationsTradingMarketAccountsByName,
} from './utils';

export function CustomerAggregations() {
  const { marketAccountsByName } = useMarketAccountsContext();
  const { marketDisplayNameByName } = useMarketsContext();
  const { aggregationsByName } = useAggregationsContext();
  const { data: aggregationsWithAccounts = EMPTY_ARRAY } = useAggregationsQuery();
  const { allowDeleteEntity, blotterTableContext } = useCustomerAggregationsDelete();

  const aggregationsFilter = usePricingAggregationsFilter({ field: 'Aggregation', label: 'Aggregation' });
  const marketAccountsFilter = useMarketAccountsFilter({
    customOptionsFilter: createIsMarketAccountInCustomerAggregationFilter(aggregationsWithAccounts),
  });
  const marketsFilter = useMarketsFilter();
  const modeFilter = useModeFilter();
  const availableFilterableProperties = useMemo(() => {
    if (aggregationsWithAccounts.length === 0) {
      return undefined;
    }
    return [aggregationsFilter, marketAccountsFilter, marketsFilter, modeFilter];
  }, [aggregationsFilter, aggregationsWithAccounts.length, marketAccountsFilter, marketsFilter, modeFilter]);
  const { externallyControlledFilter, filterableProperties } = useSpaceParameterControlledFilter(
    CUSTOMER_AGGREGATIONS_SPACE_PARAMETER_SUPPORT,
    availableFilterableProperties
  );

  const entityDrawerOptions = getCustomerAggregationsEntityDrawerOptions(
    getCustomerAggregationsMarketsDrawerOptionsFromContext(
      aggregationsByName,
      getCustomerAggregationsTradingMarketAccountsByName(marketAccountsByName),
      marketDisplayNameByName
    ),
    CUSTOMER_AGGREGATION_NAME_AND_DISPLAY_NAME_OPTIONS
  );

  return (
    <EntityAdmin<ExtendedAggregationForEA>
      title="Customer Aggregations"
      subtitle="View and manage customer aggregations and their market accounts"
      path="/aggregations"
      queryParams={{ AggregationType: 'Customer', expanded: 'true' }}
      getDeletePath={getCustomerAggregationsDeletePath}
      getPatchPath={getCustomerAggregationsPatchPath}
      getPostPath={getCustomerAggregationsPostPath}
      entityIDField="Aggregation"
      childIDField="MarketAccount"
      entityName="Aggregation"
      childEntityName="Market Account"
      getEntityDisplayName={getCustomerAggregationsEntityDisplayName(aggregationsByName)}
      allowModeSwitch={CUSTOMER_AGGREGATIONS_ALLOW_MODE_SWITCH}
      allowAddEntity={true}
      allowDeleteEntity={allowDeleteEntity}
      blotterTableContext={blotterTableContext}
      allowBulkEdit={true}
      suppressBulkDelete={true}
      persistKey="dealer/aggregations-3"
      allowEditEntity={CUSTOMER_AGGREGATIONS_ALLOW_EDIT_ENTITY}
      shouldUsePatchForPost={getCustomerAggregationsShouldUsePatchForPost}
      filterableProperties={filterableProperties}
      getEntityDrawerOptions={entityDrawerOptions}
      externallyControlledFilter={externallyControlledFilter}
      getEntityForPatchPost={getCustomerAggregationsEntityForPatchPost}
      isRowBulkEditSelectable={getCustomerAggregationsIsRowBulkEditSelectable}
      columns={CUSTOMER_AGGREGATIONS_COLUMNS}
      groupColumnDef={CUSTOMER_AGGREGATIONS_GROUP_COLUMN_DEF}
      newEntityDefault={getCustomerAggregationsNewEntityDefault}
      getResponseAsBlotterEntity={getCustomerAggregationsResponseAsBlotterEntity}
    />
  );
}
